/**
 * Sealing a secret to one specific device.
 *
 * The cloud is a courier, not a keyholder. A browser encrypts a camera
 * credential to the public key of the device that will use it; the cloud stores
 * and relays the ciphertext and can never read it, not at rest and not in
 * transit. Only that device holds the private half, and it never leaves.
 *
 * The threat this exists for is specific. Cloud-side encryption with a
 * cloud-held key would mean one breach exposes RTSP credentials and camera
 * addresses for every installation across every tenant — which for residential
 * work is not a data incident, it is someone watching people's homes. No amount
 * of remote-management convenience is worth being that single point.
 *
 * ECDH P-256 → HKDF-SHA256 → AES-256-GCM, an ECIES construction:
 *
 *   1. The sender generates an ephemeral keypair, so every message has its own
 *      key and compromising one reveals nothing about the others.
 *   2. ECDH against the device's public key yields a shared secret.
 *   3. HKDF turns that into an AES key. The raw ECDH output is not uniformly
 *      distributed and must never be used as a key directly.
 *   4. AES-GCM encrypts and authenticates, so tampering fails loudly rather
 *      than decrypting to rubbish.
 *
 * P-256 rather than X25519 because Web Crypto has supported it everywhere for
 * years; X25519 only arrived in browsers recently and this has to work in
 * whatever the installer has open.
 *
 * One implementation, used by both sides. Node exposes Web Crypto as
 * globalThis.crypto, so the browser and the agent run the same code rather than
 * two implementations that drift.
 */
/** Format marker. Any change to the construction changes this. */
const FORMAT = 'jeps1';
const CURVE = 'P-256';
const KEY_BITS = 256;
const IV_BYTES = 12;
/** Binds derived keys to this purpose, so the same secret cannot be reused elsewhere. */
const HKDF_INFO = 'jeprotect:camera-credential:v1';
/*
 * base64url via btoa/atob rather than Buffer.
 *
 * Buffer does not exist in a browser, and this module's whole premise is that the
 * browser and the agent run the same code. Buffer would have made that false at
 * runtime while still type-checking, and the failure would have appeared only
 * when someone actually tried to save a credential.
 *
 * atob throws on input that is not base64, which is load-bearing: it is what makes
 * a garbage public key fail to import rather than hash into a plausible-looking
 * fingerprint.
 */
function toBase64(bytes) {
    const view = new Uint8Array(bytes);
    let binary = '';
    // Byte at a time. These are keys and short credentials, never large buffers,
    // and spreading into fromCharCode blows the stack on anything that is.
    for (let i = 0; i < view.length; i += 1)
        binary += String.fromCharCode(view[i]);
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
function fromBase64(value) {
    const binary = atob(value.replace(/-/g, '+').replace(/_/g, '/'));
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1)
        bytes[i] = binary.charCodeAt(i);
    return bytes;
}
function subtle() {
    const api = globalThis.crypto?.subtle;
    if (!api) {
        // Browsers withhold Web Crypto on insecure origins, which would otherwise
        // surface as an inscrutable undefined-property error at the worst moment.
        throw new Error('Web Crypto is unavailable. A secure context (https) is required.');
    }
    return api;
}
/** Generates a device keypair. Called once, at first boot. */
export async function generateDeviceKeyPair() {
    const pair = (await subtle().generateKey({ name: 'ECDH', namedCurve: CURVE }, true, [
        'deriveKey',
        'deriveBits',
    ]));
    const [publicKey, privateKey] = await Promise.all([
        subtle().exportKey('spki', pair.publicKey),
        subtle().exportKey('pkcs8', pair.privateKey),
    ]);
    return { publicKey: toBase64(publicKey), privateKey: toBase64(privateKey) };
}
async function importPublic(publicKey) {
    return subtle().importKey('spki', fromBase64(publicKey), { name: 'ECDH', namedCurve: CURVE }, false, []);
}
async function importPrivate(privateKey) {
    return subtle().importKey('pkcs8', fromBase64(privateKey), { name: 'ECDH', namedCurve: CURVE }, false, ['deriveKey', 'deriveBits']);
}
/**
 * Derives the message key.
 *
 * HKDF rather than hashing the ECDH output directly: the raw shared secret is a
 * curve point and is not uniformly distributed, so it is key material, not a key.
 */
async function deriveKey(privateKey, publicKey, salt) {
    const shared = await subtle().deriveBits({ name: 'ECDH', public: publicKey }, privateKey, 256);
    const hkdfKey = await subtle().importKey('raw', shared, 'HKDF', false, ['deriveKey']);
    return subtle().deriveKey({
        name: 'HKDF',
        hash: 'SHA-256',
        salt,
        info: new TextEncoder().encode(HKDF_INFO),
    }, hkdfKey, { name: 'AES-GCM', length: KEY_BITS }, false, ['encrypt', 'decrypt']);
}
/**
 * Encrypts a secret to a device's public key.
 *
 * Returns an opaque string safe to hand to the cloud. Anyone holding it, storing
 * it, or intercepting it learns nothing without the device's private key.
 */
export async function sealToDevice(devicePublicKey, plaintext) {
    if (typeof plaintext !== 'string' || plaintext.length === 0) {
        throw new TypeError('refusing to seal an empty value');
    }
    const recipient = await importPublic(devicePublicKey);
    // Ephemeral: every message gets its own keypair, so compromising one reveals
    // nothing about any other.
    const ephemeral = (await subtle().generateKey({ name: 'ECDH', namedCurve: CURVE }, true, [
        'deriveBits',
        'deriveKey',
    ]));
    const iv = globalThis.crypto.getRandomValues(new Uint8Array(IV_BYTES));
    const key = await deriveKey(ephemeral.privateKey, recipient, iv);
    const ciphertext = await subtle().encrypt({ name: 'AES-GCM', iv }, key, new TextEncoder().encode(plaintext));
    const ephemeralPublic = await subtle().exportKey('spki', ephemeral.publicKey);
    return [FORMAT, toBase64(ephemeralPublic), toBase64(iv.buffer), toBase64(ciphertext)].join('.');
}
/**
 * Decrypts a sealed secret. Device side only.
 *
 * Throws on anything wrong — wrong key, tampered ciphertext, unknown format.
 * AES-GCM authenticates, so a modified message fails rather than decrypting to
 * plausible rubbish that then ends up in an RTSP URI.
 */
export async function openSealed(devicePrivateKey, sealed) {
    if (typeof sealed !== 'string')
        throw new TypeError('sealed value must be a string');
    const parts = sealed.split('.');
    if (parts.length !== 4)
        throw new TypeError('malformed sealed value');
    const [format, ephemeralPublic, ivPart, ciphertext] = parts;
    if (format !== FORMAT) {
        throw new TypeError(`unsupported sealed format ${JSON.stringify(format)}`);
    }
    const recipient = await importPrivate(devicePrivateKey);
    const sender = await importPublic(ephemeralPublic);
    const iv = fromBase64(ivPart);
    const key = await deriveKey(recipient, sender, iv);
    const plaintext = await subtle().decrypt({ name: 'AES-GCM', iv }, key, fromBase64(ciphertext));
    return new TextDecoder().decode(plaintext);
}
/** Whether a string looks like a sealed value. Cheap shape check, not validation. */
export function isSealed(value) {
    return typeof value === 'string' && value.startsWith(`${FORMAT}.`) && value.split('.').length === 4;
}
/**
 * A stable identifier for a device public key: SHA-256 over the SPKI bytes.
 *
 * Exists to close a silent failure. A browser seals a credential to the key it
 * fetched; if that key was stale or the device has since republished, the cloud
 * happily stores ciphertext nobody can open, and the symptom is a camera that
 * never connects for a reason invisible from both ends. The sealing party sends
 * the fingerprint it sealed to, the cloud compares it against the key actually on
 * file, and a mismatch is refused at submission time instead of discovered weeks
 * later at an install.
 *
 * Imports the key first, so a fingerprint only exists for a key that genuinely
 * parses. That is deliberate: the alternative is hashing whatever bytes arrived
 * and cheerfully storing an identifier for something that could never have
 * decrypted anything.
 *
 * Not a secret. A public key is public, and this is safe to log and to display.
 */
export async function deviceKeyFingerprint(publicKey) {
    await importPublic(publicKey);
    return toBase64(await subtle().digest('SHA-256', fromBase64(publicKey)));
}
/**
 * Whether a string is a usable device public key.
 *
 * Used at the trust boundary — the claim endpoint — so a device cannot register
 * a key that will fail to import in the browser later, which would present as
 * "credentials cannot be saved for this device" with no explanation.
 */
export async function isDevicePublicKey(value) {
    if (typeof value !== 'string' || value.length === 0)
        return false;
    try {
        await importPublic(value);
        return true;
    }
    catch {
        return false;
    }
}
//# sourceMappingURL=sealed-box.js.map